import React, { useCallback, useMemo, useRef, useState } from 'react';
import { autoPlacement, autoUpdate, offset, shift, useFloating } from '@floating-ui/react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import DatePickerInput from 'components/form/DatePickerInput.component';
import DropdownInput from 'components/form/DropdownInput.component';
import Icon from 'components/generic/Icon.component';
import FLAG from 'config/flag.singleton';
import useOnClickOutside from 'hooks/useOnClickOutsideRef.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromAnyDateToDate, fromAnyDateToIso, fromAnyDateToIsoDayBgn, fromAnyDateToIsoDayEnd } from 'utils/converters/time/from.util';
import { determineMidnightUTC } from 'utils/converters/time/intl.util';
import { timeAddMinutes } from 'utils/date/manipulate.util';
import { nowAsDate } from 'utils/date/now.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isNotSafeInteger from 'utils/predicates/number/isNotSafeInteger.util';
import isDateWorthy from 'utils/predicates/time/isDateWorthy.util';
import isNotDateWorthy from 'utils/predicates/time/isNotDateWorthy.util';
import isValidDateObject from 'utils/predicates/time/isValidDateObject.util';

import CN from './CalendarRangedInput.module.scss';

const L = logger('CalendarRangedInput'); // eslint-disable-line no-unused-vars

const MODE = Object.freeze({
  bgn: 'bgn',
  end: 'end',
});

const TYPE = Object.freeze({
  apply: 'apply',
  cancel: 'cancel',
  open: 'open',
});

// TODO: Localise months
const MONTHS = Object.freeze(['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);

const SlickHeader = (props) => {
  const {
    date: dateFromProps,
    changeYear = alwaysNoop,
    changeMonth = alwaysNoop,
    decreaseMonth = alwaysNoop,
    increaseMonth = alwaysNoop,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  } = props ?? {};

  const date = isValidDateObject(fromAnyDateToDate(dateFromProps)) ? fromAnyDateToDate(dateFromProps) : nowAsDate();
  const year = date.getFullYear();
  const month = MONTHS[date.getMonth()];

  const dropdownSelected = useMemo(() => ({ value: month, description: `${month} ${year}` }), [month, year]);

  const dropdownOptions = useMemo(
    () => [
      { value: year - 1, description: fromAnyToTrimmed(year - 1), isYear: true },
      ...MONTHS.map((month, value) => ({ value, description: `${month} ${year}`, isYear: false })),
      { value: year + 1, description: fromAnyToTrimmed(year + 1), isYear: true },
    ],
    [year],
  );

  const handleDropdown = useCallback(
    (ev) => {
      const { value, isYear } = ev?.raw ?? {};
      L.debug('SlickHeader()', 'handleDropdown()', isYear, value, 'ev:', ev);

      if (isNotSafeInteger(value)) L.warn('SlickHeader()', 'handleDropdown()', 'Invalid value:', value);

      if (isYear) {
        call('SlickHeader.changeYear', changeYear, value);
        call('SlickHeader.changeMonth', changeMonth, 0); // 0 is January
      } else {
        call('SlickHeader.changeMonth', changeMonth, value);
      }
      L.warn('SlickHeader()', 'handleDropdown()', 'Cannot determine month or year from value:', value);
    },
    [changeMonth, changeYear],
  );

  return (
    <div data-bem="SlickHeader" className={CN.slickHeader}>
      <Button variant="bin" tag="SlickHeader__month-prev" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
        <Icon tag="SlickHeader__month-prev" variant="arrowPrev" width="xl" />
      </Button>

      <div data-bem="SlickHeader__middle" className={CN.middle}>
        <DropdownInput tag="SlickHeader__dropdown" onChange={handleDropdown} options={dropdownOptions} value={dropdownSelected} variant="kpiPicker" />
      </div>

      <Button variant="bin" tag="SlickHeader__month-next" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        <Icon tag="SlickHeader__month-next" variant="arrowNext" width="xl" />
      </Button>
    </div>
  );
};

const offsetInterval = (options) => {
  // NOTE: offset in JS is opposite (in minutes) to the actual time zone offset (in hours)
  const offset = -1 * new Date().getTimezoneOffset();

  // NOTE: here we're offsetting the default time zone of the underlying moment library
  // ALERT: this is a brittle code, may not work in other cases or if future changes are made to the TZ/library
  const bgn = fromAnyDateToIsoDayBgn(timeAddMinutes(offset, options?.bgn));
  const end = fromAnyDateToIsoDayEnd(timeAddMinutes(offset, options?.end));

  return { offset, bgn, end };
};

const determineDisplays = (options) => {
  const { t, isBgnMissing, localBgn, isEndMissing, localEnd, labelFromProps, formatDate } = options ?? {};

  const isBgnBad = isBgnMissing || isNotDateWorthy(localBgn);
  const isEndBad = isEndMissing || isNotDateWorthy(localEnd);

  const startUTCDate = localBgn ? determineMidnightUTC(localBgn).utcNoon : null;
  const endUTCDate = localEnd ? determineMidnightUTC(localEnd).utcNoon : null;

  const formattedBgn = startUTCDate ? formatDate(startUTCDate, { hideTime: true, hideWeekday: true, hideYear: true }) : null;
  const formattedEnd = endUTCDate ? formatDate(endUTCDate, { hideTime: true, hideWeekday: true }) : null;

  const label = formattedBgn && formattedEnd ? `${formattedBgn} - ${formattedEnd}` : fromAnyToTrimmed(labelFromProps) || t('composite.calendarRangedInput.dateRange');

  const displayBgn = isBgnBad ? t('composite.calendarRangedInput.startDate') : formatDate(startUTCDate, { hideTime: true, hideWeekday: true });
  const displayEnd = isEndBad ? t('composite.calendarRangedInput.endDate') : formatDate(endUTCDate, { hideTime: true, hideWeekday: true });

  return { label, displayBgn, displayEnd };
};

const CalendarRangedInput = ({
  bgn: bgnFromProps,
  end: endFromProps,
  mode: modeFromProps,
  tag: tagFromProps,
  highlightToday,
  onApply,
  onCancel = alwaysNoop,
  onOpen = alwaysNoop,
  variant: variantFromProps,
  label: labelFromProps,
  selected,
  isModalPopup,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { formatDate } = useGilt();

  const tag = fromAnyToTrimmed(tagFromProps);
  const variant = fromAnyToTrimmed(variantFromProps);

  const bgn = fromAnyDateToIso(bgnFromProps);
  const end = fromAnyDateToIso(endFromProps);
  const [localBgn, setLocalBgn] = useState(bgn);
  const [localEnd, setLocalEnd] = useState(end);

  const { val: isEndMissing, nay: markEndPresent, yay: markEndMissing } = useTip(true);
  const { val: isBgnMissing, nay: markBgnPresent, yay: markBgnMissing } = useTip(!bgn);

  const highlightDates = useMemo(() => (highlightToday ? [nowAsDate()] : void 1), [highlightToday]);

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), shift(), autoPlacement({ allowedPlacements: ['top', 'bottom'] })],
  });
  const componentRef = useRef(null);
  const { val: isOpen, tip: toggleIsOpen, nay: close } = useTip(false);

  const [mode, setMode] = useState(MODE.end === modeFromProps ? MODE.end : MODE.bgn);
  const handleBgn = useCallback(() => void setMode(MODE.bgn), []);
  const handleEnd = useCallback(() => void setMode(MODE.end), []);

  const isModeBgn = MODE.bgn === mode;
  const isModeEnd = MODE.end === mode;

  const handlePickerSelect = useCallback(
    (ev) => {
      const iso = ev?.iso;
      L.debug('handlePickerSelect()', mode, iso);
      if (isModeBgn) {
        setLocalBgn(iso);
        markEndMissing();
        markBgnPresent();
      } else {
        markEndPresent();
      }
      setLocalEnd(iso);
      setMode(MODE.end);
    },
    [isModeBgn, markBgnPresent, markEndMissing, markEndPresent, mode],
  );

  const handlePickerChange = useCallback((ev) => FLAG.loggerSpam && L.debug('handlePickerChange()', ev), []);
  const handlePickerClose = useCallback((ev) => FLAG.loggerSpam && L.debug('handlePickerClose()', ev), []);
  const handlePickerOpen = useCallback((ev) => FLAG.loggerSpam && L.debug('handlePickerOpen()', ev), []);

  const handleToggle = useCallback(() => {
    toggleIsOpen();

    // if already open, we don't fire this event
    if (isOpen) return;

    const event = { bgn, end, mode, type: TYPE.open };
    call('CalendarRangedInput.onCancel', onOpen, event);
  }, [bgn, end, isOpen, mode, onOpen, toggleIsOpen]);

  const handleCancel = useCallback(() => {
    close();

    setMode(MODE.bgn);
    setLocalBgn(bgn);
    setLocalEnd(end);

    markBgnMissing();
    markEndMissing();

    const event = { bgn, end, mode, type: TYPE.cancel };
    L.debug('handleCancel()', 'event:', event);

    call('CalendarRangedInput.onCancel', onCancel, event);
  }, [bgn, close, end, mode, onCancel, markBgnMissing, markEndMissing]);

  const handleApply = useCallback(() => {
    close();

    const { bgn, end } = offsetInterval({ bgn: localBgn, end: localEnd });
    const event = { bgn, localBgn, end, localEnd, mode, type: TYPE.apply };
    L.debug('handleApply()', 'event:', event);

    call('CalendarRangedInput.onChange', onApply, event);
  }, [close, localBgn, localEnd, mode, onApply]);

  useOnClickOutside(componentRef, handleCancel);

  const displayOptions = { isBgnMissing, localBgn, isEndMissing, localEnd, labelFromProps, formatDate, t };
  const { label, displayBgn, displayEnd } = determineDisplays(displayOptions);

  const renderedCustomInput = (
    <div data-bem="CalendarRangedInput__buttons" className={CN.customButtons}>
      <div className={CN.buttonContainer}>
        <Button tag="CalendarRangedInput__bgn" variant="quinary" width="max" active={isModeBgn} selectedUniversal={isModeBgn} onClick={handleBgn}>
          <div className={CN.buttonContent}>
            <Icon variant="calendarSolid" width="md" />
            <span className={CN.textGap}>{displayBgn}</span>
          </div>
        </Button>
      </div>

      <div className={CN.buttonContainer}>
        <Button tag="CalendarRangedInput__end" variant="quinary" width="max" active={isModeEnd} disabled={!localBgn} selectedUniversal={isModeEnd} onClick={handleEnd}>
          <div className={CN.buttonContent}>
            <Icon variant="calendarSolid" width="md" /> <span className={CN.textGap}>{displayEnd}</span>
          </div>
        </Button>
      </div>
    </div>
  );

  const minDate = isModeEnd && isDateWorthy(localBgn) ? localBgn : void 1;

  return (
    <div
      data-bem="CalendarRangedInput"
      data-tag={tag}
      data-variant={variant}
      data-mode={mode}
      data-is-open={isOpen}
      className={cn({
        [CN.component]: true,
        [CN[`${variant}Variant`]]: variant,
        [CN.dark]: isDarkMode,
      })}
      ref={componentRef}
    >
      <Button tag="CalendarRangedInput__toggle" variant="quinary" selected={selected} onClick={handleToggle} ref={refs.setReference}>
        <span className={CN.iconGap}>
          <Icon variant="calendarPicker" width="md" />
        </span>
        <span className={CN.labelRange}>{label}</span>
      </Button>
      {isOpen && (
        <div data-bem="CalendarRangedInput__floater" className={CN.floater} ref={refs.setFloating} style={isModalPopup ? void 1 : floatingStyles}>
          <div className={CN.innerContainer}>
            {renderedCustomInput}
            <DatePickerInput
              v={3}
              inline
              customInput={<input type="hidden" />}
              renderCustomHeader={(props) => <SlickHeader {...props} />}
              startDate={localBgn}
              endDate={localEnd}
              selectsStart={isModeBgn}
              selectsEnd={isModeEnd}
              selectsRange
              onChange={handlePickerChange}
              onSelect={handlePickerSelect}
              onCalendarClose={handlePickerClose}
              onCalendarOpen={handlePickerOpen}
              highlightDates={highlightDates}
              closeOnScroll
              shouldCloseOnSelect={true}
              showMonthDropdown={false}
              showYearDropdown={false}
              minDate={minDate}
            />
          </div>
          <div data-bem="CalendarRangedInput__footer" className={CN.footer}>
            <div className={CN.footerButtons}>
              <Button tag="CalendarRangedInput__cancel" variant="quinary" width="auto" onClick={handleCancel}>
                <span className={CN.buttonGap}>{t('common.buttons.cancelUpp')}</span>
              </Button>

              <Button tag="CalendarRangedInput__apply" variant="primaryForce" disabledInvertDark width="auto" active={isModeEnd} disabled={!isModeEnd} onClick={handleApply}>
                <span className={CN.buttonGap}>{t('common.buttons.applyUpp')}</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CalendarRangedInput.MODE = MODE;
CalendarRangedInput.TYPE = TYPE;

export default CalendarRangedInput;
